import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { CheckCircle, ArrowRight, Users, FileCheck, Building } from 'lucide-react';
import { mockData } from '../mock';
import { openCalendlyPopup, CALENDLY_BOOKING_URL } from '../lib/calendly';

export const Consulting = () => {
  const getIcon = (index) => {
    const icons = [Users, FileCheck, Building];
    return icons[index] || Users;
  };

  return (
    <section id="consulting" className="py-14 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Strategic HR Consulting Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            When you need hands-on HR expertise, our fractional HR business partnering 
            provides strategic guidance tailored to your industry and business needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {mockData.consultingServices.map((service, index) => {
            const IconComponent = getIcon(index);
            
            return (
              <Card key={service.id} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader className="text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-4 bg-blue-100 rounded-full">
                      <IconComponent className="w-8 h-8 text-blue-600" />
                    </div>
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-gray-600 mt-2">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start space-x-3">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Industries We Support */}
        <div className="bg-gray-50 rounded-xl p-8 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-3">
            Industries We Support
          </h3>
          <p className="text-base text-gray-600 text-center max-w-2xl mx-auto mb-8">
            We work with businesses across industries. These are a few where we have deep, hands-on experience.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              'Manufacturing',
              'Healthcare',
              'Professional Services',
              'Technology',
              'Remote/Digital Companies',
              'Retail & Grocery',
              'Food Service & Restaurants',
              'Banking & Credit Unions',
            ].map((industry, index) => (
              <div key={index} className="text-center">
                <div className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                  <Building className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <span className="text-sm font-medium text-gray-700">{industry}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-blue-600 rounded-2xl p-8 md:p-12 text-center text-white">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to Partner with HR Experts?
          </h3>
          <p className="text-xl mb-3 text-blue-100 max-w-2xl mx-auto">
            Get tailored HR guidance for compliance, people strategy, and business growth. Book a consultation to discuss your HR needs and learn how DriveHR can support your next stage.
          </p>
          <p className="text-base mb-8 text-yellow-300 font-semibold" data-testid="consulting-free-consult-mention">
            Your first 30 minutes are on us. No obligation, no sales pitch.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 shadow-lg"
              onClick={() => openCalendlyPopup(CALENDLY_BOOKING_URL)}
              data-testid="consulting-schedule-cta"
            >
              Schedule Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};